import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BriefcaseBusiness,
  Check,
  MessageSquareText,
  Scale,
  Trash2,
  User,
  X,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import type {
  AiSuggestionDetail,
  SuggestionDetailLocationState,
} from "../types/aiSuggestion";
import { fetchSuggestionByItemId } from "../services/aiSuggestionsService";
import { getContractById } from "../services/contractService";
import type { Contract } from "../types/contract";
import { listActiveBusinessUnits } from "../services/businessUnitService";
import type { BusinessUnitOption } from "../types/businessUnit";
import {
  mergeBusinessUnitOptionsFromApi,
  resolveBusinessUnitsForApi,
  ALL_BUSINESS_UNITS_SENTINEL,
} from "../utils/businessUnitApiCodes";
import {
  applyClauseAtSuggestionIndexes,
  clearSuggestionHighlightAtIndexes,
  insertCommentAtSuggestionIndexes,
  navigateToSuggestionIndexes,
  removeClauseAtSuggestionIndexes,
  setDocumentChangeTrackingOff,
} from "../services/officeService";
import { ApiError } from "../services/apiClient";
import { postAiSuggestionReview } from "../services/aiSuggestionReviewService";
import {
  bindSharePointTargetingFromContract,
  persistHostedDocumentToSharePointAfterReview,
} from "../services/suggestionSharePointSyncService";
import {
  clauseLocateHintsFromSuggestion,
  collectClauseTextHints,
} from "../utils/clauseLocateHints";
import { clauseTripleFromSuggestion } from "../utils/clauseTripleFromSuggestion";
import { SuggestionDetailView } from "../components/organisms/SuggestionDetailView";
import type { ClauseSelection } from "../components/organisms/SuggestionDetailView";
import { BusinessUnitMultiSelect } from "../components/molecules/BusinessUnitMultiSelect";
import { DEFAULT_BUSINESS_UNIT_NAME } from "../constants/defaultBusinessUnit";
import {
  displaySectionSubtitle,
  formatConfidencePercent,
  rowContentRiskToUiBand,
} from "../utils/aiSuggestionDisplay";
import { RiskBadge } from "../components/atoms/RiskBadge";
import { getEffectiveAccess } from "../services/authSessionService";
import { canActOnSuggestionRiskLevel } from "../utils/decisionRoleRiskVisibility";

type LoadState = "loading" | "ready" | "not_found" | "error";

const showToast = (message: string): void => {
  globalThis.alert(message);
};

/** Collapse whitespace so trivial spacing edits do not toggle “edited” for KB publish. */
const normalizeClauseTextForCompare = (s: string): string =>
  s.trim().replace(/\s+/g, " ");

export const SuggestionReviewDetailPage = () => {
  const { id: contractId, suggestionItemId } = useParams<{
    id: string;
    suggestionItemId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = location.state as SuggestionDetailLocationState | null;

  const [contract, setContract] = useState<Contract | null>(null);
  const [loadState, setLoadState] = useState<LoadState>(() =>
    initialState?.suggestion?.suggestion_item_id ? "ready" : "loading",
  );
  const [suggestion, setSuggestion] = useState<AiSuggestionDetail | null>(
    () => initialState?.suggestion ?? null,
  );

  const [buRows, setBuRows] = useState<readonly BusinessUnitOption[]>([]);
  const [businessUnits, setBusinessUnits] = useState<string[]>([]);
  const buNameOptions = useMemo(() => buRows.map((o) => o.name), [buRows]);
  const [selection, setSelection] = useState<ClauseSelection>("ai");
  const [aiEditText, setAiEditText] = useState(() => {
    const st = initialState?.suggestion?.ai_suggested_text?.trim();
    return st ? st : "";
  });
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [publishKbChecked, setPublishKbChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!contractId || !suggestionItemId) {
      setLoadState("not_found");
      return;
    }
    if (initialState?.suggestion?.suggestion_item_id === suggestionItemId) {
      setLoadState("ready");
      return;
    }
    void (async () => {
      setLoadState("loading");
      const resolved = await fetchSuggestionByItemId(
        contractId,
        suggestionItemId,
      );
      if (cancelled) {
        return;
      }
      if (!resolved) {
        setLoadState("not_found");
        return;
      }
      setSuggestion(resolved.suggestion);
      setAiEditText(
        resolved.suggestion.ai_suggested_text?.trim()
          ? resolved.suggestion.ai_suggested_text
          : "",
      );
      setLoadState("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [
    contractId,
    suggestionItemId,
    initialState?.suggestion?.suggestion_item_id,
  ]);

  useEffect(() => {
    if (!contractId) {
      return;
    }
    let cancelled = false;
    void getContractById(contractId).then((c) => {
      if (!cancelled) {
        setContract(c);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [contractId]);

  useEffect(() => {
    if (contract) {
      bindSharePointTargetingFromContract(contract);
    }
  }, [contract]);

  useEffect(() => {
    let cancelled = false;
    void listActiveBusinessUnits()
      .then((list) => {
        if (cancelled) {
          return;
        }
        const merged = mergeBusinessUnitOptionsFromApi(list);
        const real = merged.filter(
          (o) => o.code !== ALL_BUSINESS_UNITS_SENTINEL.code,
        );
        const rows: BusinessUnitOption[] =
          real.length > 0
            ? [...real]
            : [
                {
                  code: DEFAULT_BUSINESS_UNIT_NAME,
                  name: DEFAULT_BUSINESS_UNIT_NAME,
                },
              ];
        setBuRows(rows);
        setBusinessUnits((prev) => {
          if (prev.length > 0) {
            return prev;
          }
          const preferred = rows.find(
            (o) => o.name === DEFAULT_BUSINESS_UNIT_NAME,
          );
          return [preferred?.name ?? rows[0].name];
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        const rows: BusinessUnitOption[] = [
          {
            code: DEFAULT_BUSINESS_UNIT_NAME,
            name: DEFAULT_BUSINESS_UNIT_NAME,
          },
        ];
        setBuRows(rows);
        setBusinessUnits([DEFAULT_BUSINESS_UNIT_NAME]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loadState !== "ready" || !suggestion) {
      return;
    }
    void navigateToSuggestionIndexes(
      suggestion.section_index,
      suggestion.content_index,
      {
        clauseTextHints: clauseLocateHintsFromSuggestion(suggestion),
      },
    );
  }, [loadState, suggestion]);

  const triple = useMemo(
    () => (suggestion ? clauseTripleFromSuggestion(suggestion) : null),
    [suggestion],
  );

  const canActOnSuggestion = useMemo(
    () =>
      suggestion
        ? canActOnSuggestionRiskLevel(
            getEffectiveAccess(),
            suggestion.content_risk_level,
          )
        : false,
    [suggestion],
  );

  const viewOnlyAction = !canActOnSuggestion;

  const approvedTextForSelection = useMemo(() => {
    if (!triple) {
      return "";
    }
    if (selection === "client") {
      return triple.clientRecommended?.trim() ? triple.clientRecommended : "";
    }
    const edited = aiEditText.trim();
    if (edited) {
      return edited;
    }
    return triple.aiRecommended?.trim() ? triple.aiRecommended : "";
  }, [aiEditText, selection, triple]);

  const isDeleteSourceSuggestion = useMemo(
    () =>
      (suggestion?.source ?? "").trim().toLowerCase() ===
      "ai_suggestion_delete",
    [suggestion?.source],
  );

  const canOfferAddToKb = useMemo(() => {
    if (selection !== "ai" || !triple) {
      return false;
    }
    const baseline = normalizeClauseTextForCompare(triple.aiRecommended ?? "");
    if (!baseline) {
      return false;
    }
    const effective = normalizeClauseTextForCompare(
      aiEditText.trim() || (triple.aiRecommended ?? "").trim(),
    );
    return effective !== baseline;
  }, [aiEditText, selection, triple]);

  useEffect(() => {
    if (!canOfferAddToKb) {
      setPublishKbChecked(false);
    }
  }, [canOfferAddToKb]);

  const goBackToList = useCallback(() => {
    if (contractId) {
      /* Replace so MemoryRouter reliably shows the list (task pane + blocking alerts can stall push navigation). */
      navigate(`/contractiq/contracts/${contractId}/review`, { replace: true });
    } else {
      navigate(-1);
    }
  }, [contractId, navigate]);

  const handleAddComment = useCallback(async () => {
    if (!suggestion) {
      return;
    }
    setBusy(true);
    try {
      const r = await insertCommentAtSuggestionIndexes(
        suggestion.section_index,
        suggestion.content_index,
        noteText,
        {
          clauseTextHints: clauseLocateHintsFromSuggestion(suggestion),
        },
      );
      if (!r.ok) {
        showToast(r.message);
      } else {
        showToast("Comment added to Word.");
        setNoteText("");
      }
    } finally {
      setBusy(false);
    }
  }, [noteText, suggestion]);

  const clearCurrentSuggestionHighlight = useCallback(async () => {
    if (!suggestion) {
      return;
    }
    const clearResult = await clearSuggestionHighlightAtIndexes(
      suggestion.section_index,
      suggestion.content_index,
      {
        clauseTextHints: clauseLocateHintsFromSuggestion(suggestion),
      },
    );
    if (!clearResult.ok) {
      console.warn(
        "Could not clear suggestion highlight after review",
        clearResult.message,
      );
    }
  }, [suggestion]);

  useEffect(() => {
    return () => {
      void clearCurrentSuggestionHighlight();
    };
  }, [clearCurrentSuggestionHighlight]);

  const handleApprove = useCallback(async () => {
    if (!suggestion || !contractId || !triple || viewOnlyAction) {
      return;
    }
    if (businessUnits.length === 0) {
      showToast("Select at least one business unit before approving.");
      return;
    }
    const approved = approvedTextForSelection.trim();
    if (!approved) {
      showToast("Choose a clause with text before approving.");
      return;
    }
    setBusy(true);
    try {
      // `approved` matches `approvedTextForSelection` — same string applied in Word and sent as `approved_text` below.
      const applyResult = await applyClauseAtSuggestionIndexes(
        suggestion.section_index,
        suggestion.content_index,
        approved,
        {
          clauseTextHints: collectClauseTextHints(
            approved,
            ...clauseLocateHintsFromSuggestion(suggestion),
          ),
        },
      );
      if (!applyResult.ok) {
        showToast(applyResult.message);
        return;
      }
      try {
        await setDocumentChangeTrackingOff();
      } catch (e) {
        console.warn("setDocumentChangeTrackingOff after approve", e);
        /* Clause is already applied; continue with server + navigation. */
      }
      const idempotencyKey =
        publishKbChecked &&
        typeof crypto !== "undefined" &&
        "randomUUID" in crypto
          ? crypto.randomUUID()
          : undefined;

      const approvePayload = (confirmBuRemoval: boolean) => ({
        action: "approve" as const,
        suggestion_item_id: suggestion.suggestion_item_id,
        status: "approved",
        suggestion_type: suggestion.suggestion_type?.trim()
          ? suggestion.suggestion_type
          : "paragraph",
        original_text: suggestion.original_text,
        client_suggested_text: triple.clientRecommended,
        ai_suggested_text: triple.aiRecommended,
        approved_text: approved,
        recommended_clause: triple.aiRecommended,
        business_unit_list: resolveBusinessUnitsForApi(businessUnits, buRows),
        publish_to_kb: publishKbChecked,
        confirm_bu_removal: confirmBuRemoval,
      });

      let reviewKb: Awaited<ReturnType<typeof postAiSuggestionReview>> | null =
        null;
      try {
        reviewKb = await postAiSuggestionReview(
          contractId,
          approvePayload(false),
          {
            idempotencyKey,
          },
        );
      } catch (err) {
        if (
          publishKbChecked &&
          err instanceof ApiError &&
          err.status === 409 &&
          err.apiCode === "BU_REMOVAL_CONFIRMATION_REQUIRED"
        ) {
          const removed = err.context?.removed_business_units;
          const removedLabel = Array.isArray(removed)
            ? removed.map(String).filter(Boolean).join(", ")
            : "";
          const confirmMsg = removedLabel
            ? `The following business units would be removed from the approved library entry: ${removedLabel}. Continue?`
            : "Some business units would be removed from the approved library entry. Continue?";
          if (!globalThis.confirm(confirmMsg)) {
            showToast("Approve cancelled — knowledge base was not updated.");
            return;
          }
          try {
            reviewKb = await postAiSuggestionReview(
              contractId,
              approvePayload(true),
              {
                idempotencyKey,
              },
            );
          } catch (err2) {
            showToast(
              err2 instanceof ApiError
                ? err2.message
                : "Could not save your review on the server. Try again.",
            );
            return;
          }
        } else {
          showToast(
            err instanceof ApiError
              ? err.message
              : "Could not save your review on the server. Try again.",
          );
          return;
        }
      }
      try {
        await persistHostedDocumentToSharePointAfterReview(
          contract
            ? {
                graphDriveId: contract.graphDriveId,
                graphItemId: contract.graphItemId,
              }
            : undefined,
        );
      } catch {
        await clearCurrentSuggestionHighlight();
        goBackToList();
        queueMicrotask(() =>
          showToast(
            "Suggestion approved. SharePoint sync failed — use Save in Word if you need the file updated in the library.",
          ),
        );
        return;
      }
      await clearCurrentSuggestionHighlight();
      goBackToList();
      const approvedMsg =
        reviewKb?.kb_publish != null
          ? "Suggestion approved and added to the knowledge base."
          : "Suggestion approved.";
      queueMicrotask(() => showToast(approvedMsg));
    } catch {
      showToast("Something went wrong while approving. Try again.");
    } finally {
      setBusy(false);
    }
  }, [
    approvedTextForSelection,
    buRows,
    businessUnits,
    contract,
    contractId,
    clearCurrentSuggestionHighlight,
    goBackToList,
    publishKbChecked,
    suggestion,
    triple,
    viewOnlyAction,
  ]);

  const handleReject = useCallback(async () => {
    if (!suggestion || !contractId || !triple || viewOnlyAction) {
      return;
    }
    setBusy(true);
    try {
      try {
        await postAiSuggestionReview(contractId, {
          action: "reject",
          suggestion_item_id: suggestion.suggestion_item_id,
          status: "rejected",
          suggestion_type: suggestion.suggestion_type?.trim()
            ? suggestion.suggestion_type
            : "paragraph",
          original_text: suggestion.original_text,
          client_suggested_text: triple.clientRecommended,
          ai_suggested_text: triple.aiRecommended,
          approved_text: null,
          recommended_clause: null,
          business_unit_list: null,
        });
      } catch {
        showToast("Could not save your review on the server. Try again.");
        return;
      }
      try {
        await persistHostedDocumentToSharePointAfterReview(
          contract
            ? {
                graphDriveId: contract.graphDriveId,
                graphItemId: contract.graphItemId,
              }
            : undefined,
        );
      } catch {
        await clearCurrentSuggestionHighlight();
        goBackToList();
        queueMicrotask(() =>
          showToast(
            "Suggestion rejected. SharePoint sync failed — use Save in Word if you need the file updated in the library.",
          ),
        );
        return;
      }
      await clearCurrentSuggestionHighlight();
      goBackToList();
      queueMicrotask(() => showToast("Suggestion rejected."));
    } catch {
      showToast("Something went wrong while rejecting. Try again.");
    } finally {
      setBusy(false);
    }
  }, [
    clearCurrentSuggestionHighlight,
    contract,
    contractId,
    goBackToList,
    suggestion,
    triple,
    viewOnlyAction,
  ]);

  const handleRemoveClause = useCallback(async () => {
    if (
      !suggestion ||
      !contractId ||
      !triple ||
      viewOnlyAction ||
      !isDeleteSourceSuggestion
    ) {
      return;
    }
    if (businessUnits.length === 0) {
      showToast("Select at least one business unit before approving.");
      return;
    }
    setBusy(true);
    try {
      const removeResult = await removeClauseAtSuggestionIndexes(
        suggestion.section_index,
        suggestion.content_index,
        {
          clauseTextHints: clauseLocateHintsFromSuggestion(suggestion),
        },
      );
      if (!removeResult.ok) {
        showToast(removeResult.message);
        return;
      }
      try {
        await setDocumentChangeTrackingOff();
      } catch (e) {
        console.warn("setDocumentChangeTrackingOff after remove clause", e);
      }
      try {
        await postAiSuggestionReview(contractId, {
          action: "approve",
          suggestion_item_id: suggestion.suggestion_item_id,
          status: "approved",
          suggestion_type: suggestion.suggestion_type?.trim()
            ? suggestion.suggestion_type
            : "paragraph",
          original_text: suggestion.original_text,
          client_suggested_text: triple.clientRecommended,
          ai_suggested_text: triple.aiRecommended,
          approved_text: null,
          recommended_clause: null,
          business_unit_list: resolveBusinessUnitsForApi(businessUnits, buRows),
          publish_to_kb: false,
          confirm_bu_removal: false,
        });
      } catch {
        showToast("Could not save your review on the server. Try again.");
        return;
      }
      try {
        await persistHostedDocumentToSharePointAfterReview(
          contract
            ? {
                graphDriveId: contract.graphDriveId,
                graphItemId: contract.graphItemId,
              }
            : undefined,
        );
      } catch {
        await clearCurrentSuggestionHighlight();
        goBackToList();
        queueMicrotask(() =>
          showToast(
            "Clause removed and suggestion approved. SharePoint sync failed — use Save in Word if you need the file updated in the library.",
          ),
        );
        return;
      }
      await clearCurrentSuggestionHighlight();
      goBackToList();
      queueMicrotask(() =>
        showToast("Clause removed and suggestion approved."),
      );
    } catch {
      showToast("Something went wrong while removing the clause. Try again.");
    } finally {
      setBusy(false);
    }
  }, [
    buRows,
    businessUnits,
    clearCurrentSuggestionHighlight,
    contract,
    contractId,
    goBackToList,
    isDeleteSourceSuggestion,
    suggestion,
    triple,
    viewOnlyAction,
  ]);

  if (!contractId || !suggestionItemId) {
    return <p className="text-muted-foreground">Invalid route.</p>;
  }
  if (loadState === "loading" || !suggestion) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">Loading suggestion…</p>
      </div>
    );
  }
  if (loadState === "not_found") {
    return (
      <div className="p-4">
        <p className="text-destructive" role="alert">
          Suggestion not found. Open it from the review list.
        </p>
        <button
          type="button"
          className="mt-3 text-sm font-medium text-primary"
          onClick={goBackToList}
        >
          Back to list
        </button>
      </div>
    );
  }

  const emptyAi =
    !isDeleteSourceSuggestion &&
    !triple?.aiRecommended?.trim() &&
    !aiEditText.trim() &&
    selection === "ai";
  const emptyClient =
    selection === "client" && !triple?.clientRecommended?.trim();
  const cannotApprove =
    businessUnits.length === 0 ||
    (!isDeleteSourceSuggestion && !approvedTextForSelection.trim());
  const cannotRemoveClause = businessUnits.length === 0;
  const band = rowContentRiskToUiBand(suggestion.content_risk_level);
  const sectionSubtitle = displaySectionSubtitle(suggestion.section_name);
  const riskVisualByBand = {
    legal: {
      Icon: Scale,
      shellClass: "bg-risk-legal-bg text-risk-legal",
    },
    business: {
      Icon: BriefcaseBusiness,
      shellClass: "bg-risk-business-bg text-risk-business",
    },
    exec: {
      Icon: User,
      shellClass: "bg-risk-exec-bg text-risk-exec",
    },
    red: {
      Icon: Scale,
      shellClass: "bg-risk-red-bg text-risk-red",
    },
    amber: {
      Icon: BriefcaseBusiness,
      shellClass: "bg-risk-amber-bg text-risk-amber",
    },
    green: {
      Icon: User,
      shellClass: "bg-risk-green-bg text-risk-green",
    },
  } as const;
  const { Icon: HeaderRiskIcon, shellClass: headerRiskClass } =
    riskVisualByBand[band];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="page-section flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-2">
        <div className="rounded-xl border-border bg-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="ml-[0.5rem] text-[1rem] font-semibold leading-tight text-foreground">
                {contract?.title || "fetching contract..."}
              </h1>

              <div className="mt-2 ml-[0.5rem] flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">AI Confidence:</span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                  <RiskBadge
                    band={rowContentRiskToUiBand(suggestion.content_risk_level)}
                    percentLabel={formatConfidencePercent(
                      suggestion.confidence,
                    )}
                  />
                </span>
              </div>
              {sectionSubtitle ? (
                <p className="mt-3 ml-[0.5rem] text-[0.75rem] text-muted-foreground">
                  {sectionSubtitle}
                </p>
              ) : null}
            </div>
            <span
              className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${headerRiskClass}`}
              aria-hidden
            >
              <HeaderRiskIcon className="h-4 w-4" />
            </span>
          </div>
        </div>

        {viewOnlyAction ? (
          <p
            className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
            role="status"
          >
            View only — your decision role cannot approve or reject suggestions
            at this risk tier.
          </p>
        ) : null}

        <SuggestionDetailView
          suggestion={suggestion}
          selection={selection}
          onSelectSelection={setSelection}
          aiEditText={aiEditText}
          onAiEditText={setAiEditText}
          isDeleteSourceSuggestion={isDeleteSourceSuggestion}
          readOnly={viewOnlyAction}
        />

        {(emptyAi || emptyClient) && (
          <p className="text-sm text-destructive" role="alert">
            This suggestion is missing text for the selected option. Choose
            another clause or edit the AI text.
          </p>
        )}

        <div>
          <label
            htmlFor="detail-bu"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Business Units <span className="text-destructive">*</span>
          </label>
          <BusinessUnitMultiSelect
            id="detail-bu"
            options={buNameOptions}
            value={businessUnits}
            onChange={(next) => setBusinessUnits([...next])}
            disabled={busy || viewOnlyAction}
          />
        </div>

        {canOfferAddToKb && !viewOnlyAction ? (
          <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
            <label className="flex cursor-pointer items-start gap-2.5">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary accent-risk-exec"
                checked={publishKbChecked}
                onChange={(e) => setPublishKbChecked(e.target.checked)}
                disabled={busy}
                aria-describedby="detail-add-to-kb-help"
              />
              <span className="text-sm leading-snug">
                <span className="font-medium text-foreground">
                  Add to knowledge base
                </span>
                <span
                  id="detail-add-to-kb-help"
                  className="mt-0.5 block text-xs text-muted-foreground"
                >
                  Updates the approved library and KB ingest paths using the
                  edited AI clause and the business units selected above. Runs
                  when you choose Approve.
                </span>
              </span>
            </label>
          </div>
        ) : null}

        <div>
          <label
            htmlFor="detail-note"
            className="mb-1 block text-sm font-medium text-foreground"
          >
            Note / Comment
          </label>
          <p className="mb-1 text-xs text-muted-foreground">
            Add notes that will be added as MS Word comments.
          </p>
          <textarea
            id="detail-note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            disabled={busy}
            placeholder="Enter your note or comment…"
            className="w-full rounded-lg border border-border bg-input-background px-3 py-2 text-sm"
          />
          <button
            type="button"
            disabled={busy || noteText.trim() === ""}
            onClick={() => void handleAddComment()}
            className="mt-2 inline-flex items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 text-sm font-medium text-foreground transition-all duration-200 ease-in-out hover:bg-muted/40 disabled:opacity-50"
          >
            <MessageSquareText className="h-4 w-4" aria-hidden />
            Add Comment to Word
          </button>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 flex-shrink-0 border-t border-border bg-background/95 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur-sm">
        {viewOnlyAction ? null : (
          <div className="mx-auto flex max-w-lg gap-3 p-4 pb-0">
            <button
              type="button"
              disabled={busy || cannotApprove}
              onClick={() => void handleApprove()}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-risk-exec px-4 py-3 text-sm font-semibold text-white transition-all duration-200 ease-in-out hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-5 w-5" aria-hidden />
              Approve
            </button>
            {isDeleteSourceSuggestion ? (
              <button
                type="button"
                disabled={busy || cannotRemoveClause}
                onClick={() => void handleRemoveClause()}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition-all duration-200 ease-in-out hover:bg-gray-100 disabled:opacity-50"
              >
                <Trash2 className="h-5 w-5" aria-hidden />
                Remove
              </button>
            ) : null}
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleReject()}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-risk-legal bg-white px-4 py-3 text-sm font-semibold text-risk-legal transition-all duration-200 ease-in-out hover:bg-risk-legal-bg disabled:opacity-50"
            >
              <X className="h-5 w-5" aria-hidden />
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
